import { fairUseRequest } from "@/api/admin/fair-use"
import { api } from "@/lib/api-client"

// =============================================================================
// Fair use — nightly metering reconciliation (round 3, spec R7/R8, #3089).
//
// One document per UTC day comparing what the ledger recorded with what Azure
// Monitor says the deployments actually served. The admin surface is read-only
// apart from the platform-only "run it for this day" action.
//
// Its own module rather than more of `api/admin/fair-use.ts`: that file is at
// the size the ESLint limit is about, and nothing here shares a type with it —
// reconciliation is about deployments and days, not about people and months.
//
// **Every number on the wire may be `null`, and `null` means "not known from
// here" — never zero.** Azure Monitor omits a metric that has no data, the job
// runs on the leader only, and a day nobody computed is simply absent from
// `reconciliations`. Rendering either as 0 would claim nothing was spent.
// =============================================================================

/** How a deployment is billed. `ptu` is compared on tokens but not on SEK. */
export type FairUseBillingModel = "paygo" | "ptu" | (string & {})

/** One deployment inside a day's comparison. */
export type FairUseReconciliationDeployment = {
  deployment: string
  /** The logical model id the ledger writes; null when the deployment is unmatched. */
  model: string | null
  billing_model: FairUseBillingModel | null
  /** The raw Azure Monitor meters, as stored. */
  monitor: Record<string, number>
  /** The raw ledger totals, as stored. */
  ledger: Record<string, number>
  monitor_tokens: number | null
  ledger_tokens: number | null
  monitor_sek: number | null
  ledger_sek: number | null
  delta_tokens_pct: number | null
  delta_sek_pct: number | null
}

/** One measured UTC day. */
export type FairUseReconciliationRow = {
  /** `YYYY-MM-DD`, UTC. */
  day: string
  /** When the job wrote the document; null on a document that did not stamp one. */
  ts: string | null
  /** Share of the served deployments the ledger could account for. */
  coverage_pct: number | null
  /** Share of the accounted usage that carries a price. 0 is a problem, not "cheap". */
  priced_pct: number | null
  /**
   * Whether Azure Monitor answered at all for this night.
   *
   * `false` means the whole comparison had no reference — the deltas are null
   * for a reason that is not "we agree". `null` on a document written before
   * the job stamped it.
   */
  monitor_available: boolean | null
  /**
   * Share of the Monitor volume the day's difference was computed over.
   *
   * One-sided rows (an unmapped deployment, an image deployment with no token
   * meter) are left OUT of the comparison instead of voiding it, and this is
   * how much of the night that left in. `null` when nothing was measurable.
   */
  compared_pct: number | null
  monitor_tokens: number | null
  ledger_tokens: number | null
  monitor_sek: number | null
  ledger_sek: number | null
  delta_tokens_pct: number | null
  delta_sek_pct: number | null
  /** Either delta outside its threshold. Unknown deltas are not drift. */
  drifting: boolean
  /** True when the immediately preceding day drifted too — the alert's rule. */
  drifting_two_days: boolean
  /** Deployments Azure served that no configured model claims. Shown, never dropped. */
  unmapped: string[]
  /** Free-text remarks the job recorded (a 403 on the ARM listing, a skipped database). */
  notes: string[]
  /** Deployments billed by a reservation — their SEK are excluded from the delta. */
  ptu_deployments: string[]
  deployments: FairUseReconciliationDeployment[]
}

/** The percentages the nightly alert fires on; the card colours by the same ones. */
export type FairUseReconciliationThresholds = {
  token_pct: number
  sek_pct: number
}

export type FairUseReconciliationResponse = {
  /** The window actually read (clamped server-side). */
  days: number
  from_day: string
  to_day: string
  thresholds: FairUseReconciliationThresholds
  /** Whether this deployment could run the job on request at all. */
  can_run: boolean
  /** Newest first, and only the days the job wrote. */
  reconciliations: FairUseReconciliationRow[]
}

/** Default window — one month of nights. */
export const FAIR_USE_RECONCILIATION_DAYS = 30

/**
 * Read the last `days` days of measurement coverage.
 *
 * Gated by the `fair_use` capability, so a delegated customer admin sees it
 * too: coverage is an operational fact about their own tenant, not a
 * commercial term.
 */
export async function getFairUseReconciliation(
  args: { days?: number } = {}
): Promise<FairUseReconciliationResponse> {
  const query = args.days != null ? `?days=${encodeURIComponent(String(args.days))}` : ""
  return fairUseRequest(() => api.get(`/admin/fair-use/reconciliation${query}`))
}

/**
 * Run the measurement for one day (default: yesterday, UTC).
 *
 * Platform admins only — a re-run REWRITES that day's document. Answers 202 as
 * soon as the run is scheduled; the result arrives in a later read, not in
 * this response.
 */
export async function runFairUseReconciliation(
  args: { day?: string } = {}
): Promise<{ accepted: boolean; day: string }> {
  const query = args.day ? `?day=${encodeURIComponent(args.day)}` : ""
  return fairUseRequest(() => api.post(`/admin/fair-use/reconciliation/run${query}`))
}
